import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Trash2, Check, ClipboardList, Package, Boxes, X, PlusCircle, Camera, Calculator as CalcIcon } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { CameraScan } from '../components/CameraScan'
import { Calculator } from '../components/Calculator'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'

type Prod = { productId: string; sku: string; name: string; barcode: string | null; unitsPerBox: number | null; saldo: number }
type Kind = 'pallet' | 'fracionamento'
type Entry = {
  id: string
  kind: Kind
  locationLabel: string
  productId: string
  sku: string
  name: string
  pallets: number
  boxes: number
  unitsPerBox: number
  looseUnits: number
  lotNumber?: string
  expiryDate?: string
  total: number
}

const uid = () => Math.random().toString(36).slice(2, 10)

/** Soma expressões tipo "5+7+3" (ajuda a contar pilhas de um palete fracionado). */
function sumExpr(s: string): number {
  return s.split('+').reduce((a, p) => a + (Number(p.trim()) || 0), 0)
}

export function ContagemMobilePage({ forceMode, scope }: { forceMode?: 'conference' | 'count'; scope?: 'fracionamento' | 'armazem' }) {
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  // grava = contagem que altera estoque (só admin). Senão = conferência (compara).
  const isAdmin = forceMode ? forceMode === 'count' : user?.role === 'admin'
  // Conferência com escopo: trava o tipo (fracionamento = vendável; armazem = paletes)
  // e isola a sessão por escopo (a diária não mistura com a semanal).
  const lockedKind: Kind | undefined = scope === 'armazem' ? 'pallet' : scope === 'fracionamento' ? 'fracionamento' : undefined
  const scopeTitle = scope === 'armazem' ? 'Conf. Armazém' : scope === 'fracionamento' ? 'Conf. Fracionamento' : null
  const storeKey = `contagem_mobile_${tenantId}${scope ? '_' + scope : ''}`

  const [entries, setEntries] = useState<Entry[]>([])
  const [view, setView] = useState<'lancar' | 'conferencia'>('lancar')
  const [kind, setKind] = useState<Kind>(lockedKind ?? 'pallet')
  const [location, setLocation] = useState('')
  const [term, setTerm] = useState('')
  const [sel, setSel] = useState<Prod | null>(null)
  const [pallets, setPallets] = useState('1')
  const [boxes, setBoxes] = useState('')
  const [upc, setUpc] = useState('')
  const [loose, setLoose] = useState('')
  const [showLot, setShowLot] = useState(false)
  const [lotNumber, setLotNumber] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [todos, setTodos] = useState(false) // lista sem busca: só com saldo (padrão) ou o catálogo todo
  const [scanMsg, setScanMsg] = useState<string | null>(null)
  const [calcFor, setCalcFor] = useState<'boxes' | 'loose' | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const { data: prods = [] } = useQuery<Prod[]>({
    queryKey: ['count-sheet', tenantId],
    queryFn: async () => (await api.get('/stock/count-sheet', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  // Carrega/persiste a sessão no aparelho
  useEffect(() => {
    if (!tenantId) return
    try { const s = localStorage.getItem(storeKey); setEntries(s ? JSON.parse(s) : []) } catch { setEntries([]) }
  }, [tenantId, storeKey])
  useEffect(() => {
    if (!tenantId) return
    try { localStorage.setItem(storeKey, JSON.stringify(entries)) } catch { /* ignore */ }
  }, [entries, tenantId, storeKey])

  // O que ja foi contado nesta sessao — evita recontar o mesmo SKU sem perceber.
  const contados = useMemo(() => new Set(entries.map(e => e.productId)), [entries])

  // Ordem natural: SKU numerico ordena como numero. Sem isto, 100 vem antes de 11.
  const porSku = (a: Prod, b: Prod) => a.sku.localeCompare(b.sku, 'pt-BR', { numeric: true })

  const LIMITE = 10
  // Sem busca, a lista mostra o que o sistema acredita estar na prateleira
  // (saldo > 0) — depois do sync do ERP o catalogo tem variantes demais para a
  // primeira tela ser util. A BUSCA sempre varre o catalogo inteiro, para o
  // produto achado na prateleira com saldo zero continuar contavel.
  const { matches, achados } = useMemo(() => {
    const t = term.trim().toLowerCase()
    const base = t
      ? prods.filter(p => p.sku.toLowerCase().includes(t) || p.name.toLowerCase().includes(t))
      : todos ? prods : prods.filter(p => p.saldo > 0)
    const ord = [...base].sort((a, b) => {
      if (t) {
        const rank = (p: Prod) => p.sku.toLowerCase() === t ? 0 : p.sku.toLowerCase().startsWith(t) ? 1 : 2
        const d = rank(a) - rank(b)
        if (d !== 0) return d
      }
      return porSku(a, b)
    })
    return { matches: ord.slice(0, LIMITE), achados: ord.length }
  }, [term, prods, todos])

  const nPal = kind === 'pallet' ? Math.max(1, Number(pallets) || 1) : 1
  const nBox = sumExpr(boxes)
  const nUpc = Number(upc) || 0
  const nLoose = sumExpr(loose)
  // Palete/armazém: paletes × caixas × un/caixa + avulsas. Fracionamento (vendável,
  // solto): só unidades — sem caixa nem palete.
  const total = kind === 'pallet' ? nPal * nBox * nUpc + nLoose : nLoose

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  function pick(p: Prod) {
    setSel(p); setTerm('')
    setUpc(p.unitsPerBox ? String(p.unitsPerBox) : '')
    setBoxes(''); setLoose('')
  }

  function onScanProduct(text: string) {
    const t = text.trim()
    const p = prods.find(x => (x.barcode && x.barcode.trim() === t) || x.sku.trim() === t)
    if (p) { pick(p); setScanning(false); setScanMsg(null) }
    else setScanMsg(`Código não cadastrado: ${t}`)
  }

  function add() {
    if (!sel || total <= 0) return
    const entry: Entry = {
      id: uid(), kind, locationLabel: location.trim(),
      productId: sel.productId, sku: sel.sku, name: sel.name,
      pallets: nPal, boxes: nBox, unitsPerBox: nUpc, looseUnits: nLoose,
      lotNumber: lotNumber.trim() || undefined,
      expiryDate: expiryDate || undefined,
      total,
    }
    setEntries(arr => [entry, ...arr])
    // Mantém local + tipo. Limpa produto/quantidades/lote.
    setSel(null); setPallets('1'); setBoxes(''); setUpc(''); setLoose('')
    setLotNumber(''); setExpiryDate(''); setShowLot(false)
    setMsg(null)
    setTimeout(() => searchRef.current?.focus(), 50)
  }

  function removeEntry(id: string) { setEntries(arr => arr.filter(e => e.id !== id)) }

  const totalUn = entries.reduce((s, e) => s + e.total, 0)

  async function finalizar() {
    if (!entries.length || busy) return
    const confirmMsg = isAdmin
      ? `Finalizar contagem? ${entries.length} lançamento(s), ${totalUn} unidade(s) serão GRAVADOS no estoque.`
      : `Finalizar conferência? ${entries.length} lançamento(s) serão comparados com o sistema. NÃO altera o estoque — divergências viram alerta.`
    if (!window.confirm(confirmMsg)) return
    setBusy(true); setMsg(null)
    try {
      const payload = {
        tenantId,
        scope,
        entries: entries.map(e => ({
          kind: e.kind, locationLabel: e.locationLabel, productId: e.productId,
          pallets: e.pallets, boxes: e.boxes, unitsPerBox: e.unitsPerBox, looseUnits: e.looseUnits,
          lotNumber: e.lotNumber, expiryDate: e.expiryDate,
        })),
      }
      if (isAdmin) {
        const { data: r } = await api.post('/stock/mobile-count', payload)
        setMsg(`Contagem gravada: ${r.paletesCriados} palete(s), ${r.fracLancados} lançamento(s) de fracionamento, ${r.unidades} unidade(s).`)
      } else {
        const { data: r } = await api.post('/stock/mobile-conference', payload)
        setMsg(`Conferência concluída: ${r.checked} SKU(s) conferido(s), ${r.divergent} com divergência, ${r.matched} OK. Divergências no menu "Divergências".`)
      }
      setEntries([])
      setView('lancar')
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao finalizar')
    } finally { setBusy(false) }
  }

  return (
    <div className="mx-auto max-w-md space-y-4 pb-28">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl text-gray-900">{scopeTitle ?? (isAdmin ? 'Contagem' : 'Conferência')}</h2>
          {!isAdmin && <p className="text-xs text-gray-500">
            {scope === 'fracionamento' ? 'saldo vendável (GERAL) · diária · não altera estoque'
              : scope === 'armazem' ? 'reserva em paletes · semanal · não altera estoque'
              : 'compara com o sistema · não altera estoque'}
          </p>}
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{entries.length} · {totalUn} un</span>
      </div>

      {msg && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">{msg}</div>}

      {/* Passo a passo da rotina */}
      <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
        {isAdmin
          ? '1) Conte cada local e adicione. 2) Revise. 3) "Finalizar contagem" grava no estoque.'
          : '1) Conte cada local e adicione. 2) Revise. 3) "Finalizar conferência" compara com o sistema — divergências viram alerta (não altera o estoque).'}
      </p>

      {/* Abas */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        {(['lancar', 'conferencia'] as const).map(v => (
          <button key={v} onClick={() => setView(v)}
            className={'flex-1 rounded-md py-2 text-sm font-medium transition-colors ' + (view === v ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500')}>
            {v === 'lancar' ? 'Contar' : `Revisar (${entries.length})`}
          </button>
        ))}
      </div>

      {view === 'lancar' ? (
        <>
          {/* Tipo — travado quando a conferência tem escopo (fracionamento/armazém) */}
          {lockedKind ? (
            <div className={'flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold ' + (lockedKind === 'pallet' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-amber-500 bg-amber-50 text-amber-700')}>
              {lockedKind === 'pallet' ? <Boxes className="h-5 w-5" /> : <Package className="h-5 w-5" />}
              {lockedKind === 'pallet' ? 'Armazém (paletes)' : 'Fracionamento (vendável)'}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setKind('pallet')}
                className={'flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold ' + (kind === 'pallet' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500')}>
                <Boxes className="h-5 w-5" /> Palete (reserva)
              </button>
              <button onClick={() => setKind('fracionamento')}
                className={'flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold ' + (kind === 'fracionamento' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500')}>
                <Package className="h-5 w-5" /> Fracionamento
              </button>
            </div>
          )}
          <p className="-mt-1 text-xs text-gray-400">
            {kind === 'pallet'
              ? 'Cada item = UM palete específico. Conte as caixas deste palete e adicione (repita por palete).'
              : 'Fracionamento (solto): SUBSTITUI o saldo vendável deste SKU pelo total contado.'}
          </p>

          {/* Local */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Onde estou</label>
            <input value={location} onChange={e => setLocation(e.target.value)}
              placeholder={kind === 'pallet' ? 'Ex: A-01, Rua 3, Box 12…' : 'Ex: Prateleira F3…'}
              className="h-12 w-full rounded-xl border border-gray-300 px-4 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
          </div>

          {/* Produto */}
          {sel ? (
            <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-sm font-bold text-blue-800">{sel.sku}</p>
                <p className="truncate text-sm text-gray-700">{sel.name}</p>
              </div>
              <button onClick={() => setSel(null)} className="text-gray-400"><X className="h-5 w-5" /></button>
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input ref={searchRef} value={term} onChange={e => setTerm(e.target.value)}
                    placeholder="Buscar produto (SKU ou nome)"
                    className="h-12 w-full rounded-xl border border-gray-300 pl-10 pr-4 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
                <button type="button" onClick={() => { setScanMsg(null); setScanning(true) }}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-blue-500 text-blue-600 active:bg-blue-50" aria-label="Escanear">
                  <Camera className="h-6 w-6" />
                </button>
              </div>
              {scanMsg && <p className="mt-1 break-all text-xs text-amber-600">{scanMsg}</p>}

              <div className="mt-1.5 flex items-center justify-between text-xs">
                <span className="text-gray-400">
                  {term
                    ? `${achados} encontrado(s)${achados > matches.length ? ` · mostrando ${matches.length}` : ''}`
                    : todos
                      ? `catálogo inteiro · ${achados} produtos`
                      : `${achados} com saldo aqui`}
                </span>
                {!term && (
                  <button type="button" onClick={() => setTodos(v => !v)} className="font-medium text-blue-600">
                    {todos ? 'só com saldo' : 'ver todos'}
                  </button>
                )}
              </div>

              {(term || matches.length > 0) && (
                <div className="mt-1 overflow-hidden rounded-xl border border-gray-200">
                  {matches.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-400">
                      {term ? 'Nenhum produto com esse termo.' : 'Nenhum produto com saldo — use "ver todos" ou busque pelo SKU.'}
                    </p>
                  ) : matches.map(p => (
                    <button key={p.productId} onClick={() => pick(p)}
                      className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 active:bg-blue-50">
                      {contados.has(p.productId)
                        ? <Check className="h-4 w-4 shrink-0 text-green-600" />
                        : <span className="w-4 shrink-0" />}
                      <span className="font-mono text-sm font-semibold text-gray-800">{p.sku}</span>
                      <span className="flex-1 truncate text-sm text-gray-600">{p.name}</span>
                      {p.unitsPerBox ? <span className="text-xs text-gray-400">{p.unitsPerBox}/cx</span> : null}
                    </button>
                  ))}
                </div>
              )}
              {term && achados > matches.length && (
                <p className="mt-1 text-center text-[11px] text-gray-400">
                  Refine a busca para ver os outros {achados - matches.length}.
                </p>
              )}
            </div>
          )}

          {/* Quantidades */}
          {sel && (
            <div className="space-y-3 rounded-xl border border-gray-200 p-4">
              {kind === 'pallet' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <NumField label="Caixas neste palete" value={boxes} onChange={setBoxes} autoFocus allowSum onCalc={() => setCalcFor('boxes')} />
                    <NumField label="Un / caixa" value={upc} onChange={setUpc} />
                  </div>
                  <NumField label="Unidades avulsas (soltas)" value={loose} onChange={setLoose} allowSum onCalc={() => setCalcFor('loose')} />
                </>
              ) : (
                // Fracionamento (vendável/solto): conta unidades direto, sem caixa/palete.
                <NumField label="Unidades contadas" value={loose} onChange={setLoose} autoFocus allowSum onCalc={() => setCalcFor('loose')} />
              )}
              <p className="text-center text-[11px] text-gray-400">Dica: pode somar pilhas — digite <b>5+7+3</b> que ele calcula.</p>

              {!showLot ? (
                <button onClick={() => setShowLot(true)} className="flex items-center gap-1 text-sm text-blue-600">
                  <PlusCircle className="h-4 w-4" /> Adicionar lote / validade
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Lote</label>
                    <input value={lotNumber} onChange={e => setLotNumber(e.target.value)}
                      className="h-11 w-full rounded-lg border border-gray-300 px-3 text-base focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Validade</label>
                    <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                      className="h-11 w-full rounded-lg border border-gray-300 px-3 text-base focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2">
                <span className="text-sm text-gray-500">Total</span>
                <span className="font-mono text-lg font-bold text-gray-900">{total} un</span>
              </div>
              <Button size="lg" className="w-full" disabled={total <= 0} onClick={add}>
                <Check className="h-5 w-5" /> Adicionar item contado
              </Button>
            </div>
          )}

          {/* Lançamentos desta contagem — mostra TODOS (mais novo no topo) */}
          {entries.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Contados agora ({entries.length})</p>
              <div className="space-y-1">
                {entries.map(e => <EntryRow key={e.id} e={e} onRemove={removeEntry} />)}
              </div>
            </div>
          )}
        </>
      ) : (
        <Conferencia entries={entries} onRemove={removeEntry} scope={scope} />
      )}

      {/* Barra fixa: finalizar */}
      {entries.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 p-3 backdrop-blur">
          <div className="mx-auto flex max-w-md items-center gap-3">
            <div className="text-sm">
              <span className="font-semibold text-gray-900">{entries.length}</span>
              <span className="text-gray-500"> lanç. · </span>
              <span className="font-semibold text-gray-900">{totalUn}</span>
              <span className="text-gray-500"> un</span>
            </div>
            <Button size="lg" className="ml-auto" loading={busy} onClick={finalizar}>
              <ClipboardList className="h-5 w-5" /> {isAdmin ? 'Finalizar contagem' : 'Finalizar conferência'}
            </Button>
          </div>
        </div>
      )}

      {scanning && (
        <CameraScan hint="Aponte para o código de barras do produto"
          onScan={onScanProduct} onClose={() => setScanning(false)} />
      )}

      {calcFor && (
        <Calculator
          title={calcFor === 'boxes' ? 'Caixas' : 'Unidades avulsas'}
          initial={calcFor === 'boxes' ? boxes : loose}
          onInsert={n => (calcFor === 'boxes' ? setBoxes : setLoose)(String(n))}
          onClose={() => setCalcFor(null)} />
      )}
    </div>
  )
}

function NumField({ label, value, onChange, autoFocus, allowSum, onCalc }: { label: string; value: string; onChange: (v: string) => void; autoFocus?: boolean; allowSum?: boolean; onCalc?: () => void }) {
  const showSum = allowSum && value.includes('+')
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">{label}</label>
      <div className="relative">
        <input
          type={allowSum ? 'text' : 'number'} inputMode={allowSum ? 'text' : 'numeric'} min={0} value={value} autoFocus={autoFocus}
          onChange={e => onChange(allowSum ? e.target.value.replace(/[^\d+]/g, '') : e.target.value)}
          onFocus={e => e.currentTarget.select()}
          className={'h-12 w-full rounded-lg border border-gray-300 px-3 text-center text-lg font-semibold focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50' + (onCalc ? ' pr-11' : '')} />
        {onCalc && (
          <button type="button" onClick={onCalc} aria-label="Calculadora"
            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-2 text-blue-600 active:bg-blue-50">
            <CalcIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {showSum && <p className="mt-0.5 text-center text-xs font-semibold text-blue-600">= {sumExpr(value)}</p>}
    </div>
  )
}

function EntryRow({ e, onRemove }: { e: Entry; onRemove: (id: string) => void }) {
  const breakdown = e.kind === 'pallet'
    ? `${e.pallets > 1 ? e.pallets + '×' : ''}${e.boxes} cx × ${e.unitsPerBox}${e.looseUnits ? ` + ${e.looseUnits}` : ''}`
    : `${e.boxes ? `${e.boxes} cx × ${e.unitsPerBox}` : ''}${e.looseUnits ? `${e.boxes ? ' + ' : ''}${e.looseUnits} avulsas` : ''}`
  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2">
      <span className={'flex h-7 w-7 shrink-0 items-center justify-center rounded-md ' + (e.kind === 'pallet' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600')}>
        {e.kind === 'pallet' ? <Boxes className="h-4 w-4" /> : <Package className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          <span className="font-mono font-semibold text-gray-800">{e.sku}</span>
          {e.locationLabel && <span className="ml-2 text-xs text-gray-400">@ {e.locationLabel}</span>}
        </p>
        <p className="text-xs text-gray-500">{breakdown}{e.lotNumber ? ` · lote ${e.lotNumber}` : ''}</p>
      </div>
      <span className="font-mono text-sm font-bold text-gray-900">{e.total}</span>
      <button onClick={() => onRemove(e.id)} className="text-gray-300 active:text-red-500"><Trash2 className="h-4 w-4" /></button>
    </div>
  )
}

function Conferencia({ entries, onRemove, scope }: { entries: Entry[]; onRemove: (id: string) => void; scope?: 'fracionamento' | 'armazem' }) {
  const isFrac = scope === 'fracionamento'
  const isArm = scope === 'armazem'
  const bySku = useMemo(() => {
    const m = new Map<string, { sku: string; name: string; boxes: number; units: number; palletUn: number; fracUn: number; count: number }>()
    for (const e of entries) {
      const cur = m.get(e.sku) ?? { sku: e.sku, name: e.name, boxes: 0, units: 0, palletUn: 0, fracUn: 0, count: 0 }
      cur.boxes += e.pallets * e.boxes
      cur.units += e.total
      if (e.kind === 'pallet') cur.palletUn += e.total; else cur.fracUn += e.total
      cur.count += 1
      m.set(e.sku, cur)
    }
    return [...m.values()].sort((a, b) => a.sku.localeCompare(b.sku))
  }, [entries])

  if (entries.length === 0) {
    return <p className="py-10 text-center text-sm text-gray-400">Nada contado ainda. Volte para "Contar".</p>
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Resumo por SKU ({bySku.length})</p>
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                {!isFrac && <th className="px-3 py-2 text-right font-medium text-gray-600">Cx</th>}
                {!isFrac && <th className="px-3 py-2 text-right font-medium text-gray-600">Palete</th>}
                {!isArm && !isFrac && <th className="px-3 py-2 text-right font-medium text-gray-600">Frac.</th>}
                <th className="px-3 py-2 text-right font-medium text-gray-600">{isFrac ? 'Unidades' : 'Total'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bySku.map(r => (
                <tr key={r.sku}>
                  <td className="px-3 py-2"><span className="font-mono font-semibold">{r.sku}</span></td>
                  {!isFrac && <td className="px-3 py-2 text-right font-mono text-gray-500">{r.boxes}</td>}
                  {!isFrac && <td className="px-3 py-2 text-right font-mono text-blue-700">{r.palletUn || ''}</td>}
                  {!isArm && !isFrac && <td className="px-3 py-2 text-right font-mono text-amber-700">{r.fracUn || ''}</td>}
                  <td className="px-3 py-2 text-right font-mono font-bold">{r.units}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Contados ({entries.length})</p>
        <div className="space-y-1">
          {entries.map(e => <EntryRow key={e.id} e={e} onRemove={onRemove} />)}
        </div>
      </div>
    </div>
  )
}
